import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { eventService } from '@/lib/api/event.service';
import { PageContainer } from '@/components/layout/PageContainer';

export function EventServicesRedirect() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['services']);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function redirectToCelebrationSummary() {
      const eventId = Number(id);
      if (!eventId) {
        if (mounted) setError(t('services:redirect.errors.invalidEventId'));
        return;
      }

      try {
        const event = await eventService.getEvent(eventId);
        if (!event.celebration_id) {
          throw new Error(t('services:redirect.errors.notLinked'));
        }

        navigate(`/celebrations/${event.celebration_id}/services`, { replace: true });
      } catch (e: any) {
        if (mounted) {
          setError(e.message ?? t('services:redirect.errors.failedOpen'));
        }
      }
    }

    redirectToCelebrationSummary();

    return () => {
      mounted = false;
    };
  }, [id, navigate]);

  if (error) {
    return (
      <PageContainer width="wide">
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
        <button
          type="button"
          onClick={() => navigate(id ? `/events/${id}` : '/events')}
          className="mt-4 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {t('services:redirect.backToEvent')}
        </button>
      </PageContainer>
    );
  }

  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <span className="ml-2 text-sm text-gray-500">{t('services:redirect.opening')}</span>
    </div>
  );
}
